import type { DraggableProvided } from '@hello-pangea/dnd'
import type { SingleChoiceItem } from '@vedalib/editor/lib/elements'
import { TestAnswerFeedbackDisplayOption } from '@vedalib/editor/lib/elements'
import { textToRtValue } from '@vedalib/rich-text'
import type { RichTextValue } from '@vedalib/rich-text'
import cn from 'classnames'
import * as R from 'ramda'
import React from 'react'

import TestFeedback from 'components/Editor/EditorElements/courseKit/TestFeedback'
import {
  getFeedbackValue,
  onChangeRTOptionFb,
} from 'components/Editor/EditorElements/utils/getFeedbackValue'
import AbsolutePortal from 'components/Editor/components/AbsolutePortal'
import RichText from 'components/form/RichText/RichText'
import { useGetRichTextProps } from 'components/form/RichText/useGetRichTextProps'
import { IconButton } from 'components/uiKit/Button'
import { Tooltip } from 'components/uiKit/Dropdown'
import Icon from 'components/uiKit/Icon'
import { KitSize } from 'components/uiKit/KitTypes'
import { PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'
import { extendTestData } from 'utils/test/qaData'

import type { TestSingleChoiceElementType } from '../testSingleChoiceTypes'
import Radio from './Radio'
import * as s from './TestSingleChoiceElement.module.scss'

interface ITestSingleChoiceElementEditorItemProps
  extends Pick<
    React.ComponentProps<TestSingleChoiceElementType>,
    'element' | 'mode' | 'styles' | 'waiting' | 'onChange' | 'font' | 'feedback'
  > {
  index: number
  provided: DraggableProvided
  showControls?: boolean
}

const TestSingleChoiceElementEditorItem: React.FC<ITestSingleChoiceElementEditorItemProps> = ({
  showControls,
  provided,
  index,
  element,
  mode,
  styles,
  waiting,
  font,
  feedback,
  onChange,
}) => {
  const { id: elementId, value } = element
  const { items } = value
  const feedbackValue = getFeedbackValue(value)

  const { isActiveElement, activeIndex, onLabelSelect, isFill } = useGetRichTextProps({
    elementId,
    mode,
  })

  const onChangeLabel = (labelValue: RichTextValue, index: number) =>
    onChange?.(R.assocPath(['items', index, 'label'], labelValue, value))

  const handleDelete = (item: SingleChoiceItem) => () =>
    onChange?.(
      R.assoc(
        'items',
        value.items.filter(({ value: id }) => id !== item.value),
        value,
      ),
    )

  const handleSetValid = (index: number) => () =>
    onChange?.(
      R.assoc(
        'items',
        value.items.map((item, i) => ({ ...item, isValid: i === index })),
        value,
      ),
    )

  return (
    <div
      ref={provided.innerRef}
      {...provided.draggableProps}
      className={cn(s.wrapItem, { [s.active]: activeIndex === index.toString() })}
      key={items[index].value}
      style={{
        ...provided.draggableProps.style,
        marginBottom: styles.offset.marginTop,
      }}
    >
      <TestFeedback
        feedbackValue={feedbackValue}
        font={font}
        isTrueAnswer={items[index]?.isValid}
        mode={mode}
        onChange={onChangeRTOptionFb(index, value, onChange)}
        rtProps={{
          activeIndex,
          isActiveElement,
          onLabelSelect,
          isFill,
          value: items[index]?.feedback,
        }}
        showFeedback={
          value.displayFeedback && value.displayOption !== TestAnswerFeedbackDisplayOption.general
        }
        styles={feedback}
        showAnswerFb
      >
        <Radio
          checked={items[index]?.isValid}
          setIsValid={handleSetValid(index)}
          styles={styles}
          testData={extendTestData({
            index,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            label: items[index]?.label ? (items[index]?.label[0] as any).children[0].text : '',
          })}
          value={items[index]?.value}
          isEditor
        >
          <span onMouseDown={() => onLabelSelect(index.toString())}>
            <RichText
              active={isActiveElement && activeIndex === index.toString()}
              disabled={!isFill}
              isPreview={mode?.previewMode !== PreviewMode.editor}
              name={`radioItem.${index}`}
              onChange={(val) => onChangeLabel(val, index)}
              styles={font}
              value={items[index]?.label || textToRtValue('')}
              waiting={waiting}
            />
          </span>
        </Radio>
      </TestFeedback>
      <div {...provided.dragHandleProps} className={cn(s.dnd, isFill && isActiveElement && s.show)}>
        <Icon name='builderDrugAndDrop' size={KitSize.XS} />
      </div>
      {isFill && isActiveElement && showControls && (
        <AbsolutePortal
          name='deleteMultipleChoiceItem'
          placement='right'
          translateX='3px'
          translateY='-50%'
        >
          <Tooltip overlay={t('uiKit.tooltip.deleteAnswer')}>
            <IconButton
              disabled={value.items.length < 3}
              icon='otherClose'
              name='deleteMultipleChoiceItem'
              onClick={handleDelete(items[index])}
              size={KitSize.XS}
              styleType='secondary'
            />
          </Tooltip>
        </AbsolutePortal>
      )}
    </div>
  )
}

export default TestSingleChoiceElementEditorItem
